import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NgxMaskDirective } from 'ngx-mask';
import { Subject, Subscription, debounceTime, distinctUntilChanged, forkJoin, switchMap } from 'rxjs';
import { Patterns } from '@app/constants/patterns.constant';
import { FeedbackService } from '@app/core/services';
import { IAccountSearchModel } from '@app/interfaces/lsb/account-search';
import { ParentAccount } from '@app/modules/fund-management/accounts/models/accounts';
import { AccountService, AccountStructureService } from '@app/shared/services';
import * as Transforms from '@app/utils/transforms';
import { ModalComponent } from '../modal/modal.component';

enum SearchTypes {
	AccountNumber = 'AccountNumber',
	Description = 'Description',
}

@Component({
	selector: 'account-search',
	templateUrl: './account-search.component.html',
	styleUrls: ['./account-search.component.less'],
	standalone: true,
	imports: [
		ModalComponent,
		NgIf,
		MatFormFieldModule,
		NgClass,
		MatInputModule,
		FormsModule,
		MatIconModule,
		NgxMaskDirective,
		MatRadioModule,
		MatTableModule,
		MatSortModule,
		MatPaginatorModule,
	],
})
export class AccountSearchComponent implements OnInit, OnDestroy {
	private searchSubject = new Subject<string>();
	private searchSubscription: Subscription;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;
	displayedColumns: string[] = [SearchTypes.AccountNumber, SearchTypes.Description];
	dataSource = new MatTableDataSource<ParentAccount>();
	data: ParentAccount[] = [];
	accountSearch = SearchTypes.AccountNumber;
	searchInput = '';
	searchAccountInput: string;
	accountMask: string;
	customPatterns = { 0: { pattern: new RegExp(Patterns.alphaNumericAccount) } };
	clickedRows = new Set<ParentAccount>();
	loading = true;

	constructor(
		protected readonly dialog: MatDialog,
		protected readonly accountService: AccountService,
		protected readonly accountStructureService: AccountStructureService,
		protected readonly cd: ChangeDetectorRef,
		protected readonly feedbackService: FeedbackService,
		public dialogRef: MatDialogRef<AccountSearchComponent>
	) {}

	ngOnInit(): void {
		forkJoin([
			this.accountService.searchAccounts({
				AccountNumber: '',
				Limit: 1000,
				AccountTypeIds: [],
				Filter: false,
			} as IAccountSearchModel),
			this.accountStructureService.getStructure(),
		]).subscribe(([accounts, accountInformation]) => {
			this.data = accounts.accounts;
			this.dataSource.data = accounts.accounts;
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;
			this.accountMask = accountInformation.accountStructure.UnifiedAccountMask;
			this.searchAccountInput = Transforms.toQuestionMask(this.accountMask);
			this.loading = false;
		});
		this.searchSubscription = this.searchSubject
			.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				switchMap((term) =>
					this.accountService.searchAccounts({
						AccountNumber: term,
						Limit: 1000,
						AccountTypeIds: [],
						Filter: false,
						OnlyActive: false,
						BankId: null,
					} as IAccountSearchModel)
				)
			)
			.subscribe({
				next: (resp) => {
					this.data = resp.accounts;
					this.dataSource.data = resp.accounts;
					this.dataSource.sort = this.sort;
					this.dataSource.paginator = this.paginator;
					if (this.accountSearch === SearchTypes.Description) {
						this.dataSource.filter = this.searchInput;
					} else {
						this.dataSource.filter = '';
					}
				},
				error: (errorResponse) => {
					const errorMessage = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || '';
					this.feedbackService.error(`There was a problem loading Banks List: ${errorMessage}`);
				},
			});
	}

	applyFilter(): void {
		if (this.accountSearch !== SearchTypes.Description) {
			this.filterAccounts(this.searchAccountInput);
		} else {
			this.filterAccounts();
		}
	}

	searchAccount(): void {
		this.dataSource.filter = this.searchInput;
	}

	filterAccounts(value = ''): void {
		this.searchSubject.next(value);
	}

	selectedAccount(row: ParentAccount): void {
		this.dialogRef.close(row);
	}

	ngOnDestroy() {
		this.searchSubscription.unsubscribe();
	}
}
